import { isSupabaseConfigured, supabase } from "@/lib/supabase";

export type UploadBucket = "content-audio" | "content-docs" | "content-covers";

const STORAGE_PUBLIC_PREFIX = "/storage/v1/object/public/";

function sanitizeFileName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9._-]+/g, "-");
}

function ensureSupabase() {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error("Supabase no está configurado.");
  }
  return supabase;
}

export function getBucketObjectPathFromPublicUrl(bucket: UploadBucket, publicUrl: string): string | null {
  const marker = `${STORAGE_PUBLIC_PREFIX}${bucket}/`;
  const index = publicUrl.indexOf(marker);
  if (index < 0) return null;
  const rawPath = publicUrl.slice(index + marker.length);
  if (!rawPath) return null;
  return decodeURIComponent(rawPath);
}

export async function uploadFileToBucket(params: {
  bucket: UploadBucket;
  file: File;
  folder: string;
}) {
  const client = ensureSupabase();
  const path = `${params.folder}/${crypto.randomUUID()}-${sanitizeFileName(params.file.name)}`;
  const { error: uploadError } = await client.storage.from(params.bucket).upload(path, params.file, {
    cacheControl: "3600",
    upsert: false,
    contentType: params.file.type,
  });

  if (uploadError) {
    throw uploadError;
  }

  const { data: publicData } = client.storage.from(params.bucket).getPublicUrl(path);

  return {
    path,
    publicUrl: publicData.publicUrl,
    mimeType: params.file.type,
    sizeMb: Number((params.file.size / (1024 * 1024)).toFixed(2)),
  };
}

export async function deleteFileByPublicUrl(params: {
  bucket: UploadBucket;
  publicUrl: string;
}) {
  const client = ensureSupabase();
  const path = getBucketObjectPathFromPublicUrl(params.bucket, params.publicUrl);
  if (!path) {
    throw new Error("No se pudo derivar el path del archivo en Storage.");
  }

  const { error } = await client.storage.from(params.bucket).remove([path]);
  if (error) {
    throw error;
  }

  return path;
}

export async function createSignedFileUrl(params: {
  bucket: UploadBucket;
  publicUrl: string;
  expiresInSeconds: number;
}) {
  const client = ensureSupabase();
  const path = getBucketObjectPathFromPublicUrl(params.bucket, params.publicUrl);
  if (!path) {
    throw new Error("No se pudo derivar el path para signed URL.");
  }

  const { data, error } = await client.storage.from(params.bucket).createSignedUrl(path, params.expiresInSeconds);
  if (error) {
    throw error;
  }

  return data.signedUrl;
}
